/**
 * 注册页面
 * 支持邮箱验证码注册、邀请码、海报展示
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '@/services/authApi';
import { apiClient } from '@/services/api';
import { Loader2, Mail, Gift, CheckCircle2, XCircle, Sparkles, User, Smartphone, Download, ChevronLeft, ChevronRight, Pause } from 'lucide-react';
import type { InvitationPageConfig } from '@xiaozhi/shared-types';

/** 注册页轮播间隔下限（ms）。后台配置默认 2s 偏快，这里把注册页拉长，方便看清每张海报；
 *  若后台显式配置了更长的间隔则尊重后台值。 */
const REGISTER_INTERVAL_FLOOR_MS = 6000;
/** 判定为“点击”的最长按下时长（ms）；超过则视为长按（保存图片），不触发切换 */
const TAP_MAX_MS = 500;
/** 判定为“点击”允许的最大位移（px），超过视为拖拽/滑动，不触发切换 */
const TAP_MOVE_THRESHOLD = 10;
/** 长按保存后恢复自动轮播的延迟（ms），留出操作保存菜单的时间 */
const RESUME_DELAY_MS = 4000;

/** 兜底下载链接：当无法从后端取到最新版时使用（避免下载完全失效） */
const FALLBACK_DOWNLOAD_URL = '/api/app-version/download/1776334087899_FeyaGate-v1.0.22-release.apk';

/** 邀请注册页 App 下载卡片：整卡可点击，置于轮播图下方更醒目。
 *  downloadUrl 传入最新版下载地址；为空时回退到兜底链接。 */
function InvitationDownloadCard({ downloadUrl }: { downloadUrl?: string }) {
  const href = downloadUrl || FALLBACK_DOWNLOAD_URL;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group block p-4 rounded-2xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white shadow-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold">下载 Alita Planet App</p>
          <p className="text-white/80 text-xs">随时随地畅享 AI 对话</p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-1 bg-white/20 group-hover:bg-white/30 rounded-lg px-3 py-2 text-sm font-medium transition-colors">
          <Download className="w-4 h-4" />
          下载
        </div>
      </div>
    </a>
  );
}

/** 从整段 URL 或纯邀请码中解析出邀请码（保持大小写） */
function normalizeInvitationCodeInput(raw: string): string {
  const s = raw.trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    try {
      const u = new URL(s);
      const q =
        u.searchParams.get('invitationCode') ||
        u.searchParams.get('invitation_code') ||
        u.searchParams.get('code');
      if (q) return q.trim();
    } catch {
      /* 非标准 URL，按原样返回 */
    }
  }
  return s;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    emailCode: '',
    invitationCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [codeExpireSeconds, setCodeExpireSeconds] = useState(60);
  const [invitationCodeStatus, setInvitationCodeStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [inviterUsername, setInviterUsername] = useState('');

  // 邀请页海报配置
  const [posterConfig, setPosterConfig] = useState<InvitationPageConfig | null>(null);
  // 当前轮播到的海报索引
  const [currentPosterIndex, setCurrentPosterIndex] = useState(0);
  // 最新版 App 下载地址（动态从后端获取，避免硬编码旧版 APK）
  const [appDownloadUrl, setAppDownloadUrl] = useState<string | undefined>(undefined);
  // 点击海报暂停轮播：用户轻点图片可暂停/恢复，长按仍可保存图片
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  // 长按检测：按下达到阈值时暂停轮播（保证保存时图片不切换）
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 长按保存后恢复自动轮播的延时
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 本次按压的起始信息（时间/坐标/是否移动过），用于区分“点击 / 长按 / 拖拽”
  const pressInfoRef = useRef<{ time: number; x: number; y: number; moved: boolean; longPressFired: boolean } | null>(null);
  const hasInvitationCode = !!formData.invitationCode;

  // 从 URL 参数中读取邀请码
  useEffect(() => {
    const invitationCodeFromUrl = searchParams.get('invitationCode');
    if (invitationCodeFromUrl) {
      setFormData(prev => ({
        ...prev,
        invitationCode: normalizeInvitationCodeInput(invitationCodeFromUrl),
      }));
    }
  }, [searchParams]);

  // 加载海报配置（仅在邀请注册时）
  useEffect(() => {
    if (hasInvitationCode) {
      apiClient.getConfig().then(config => {
        setPosterConfig(config?.invitationPage || null);
      }).catch(() => {});
    }
  }, [hasInvitationCode]);

  // 获取最新版 App 下载地址（current_version_code=0 视为新用户，必返回最新版 download_url）
  useEffect(() => {
    apiClient.checkAppVersion('android', 0).then(info => {
      if (info?.downloadUrl) setAppDownloadUrl(info.downloadUrl);
    }).catch(() => {
      // 取不到时保持 undefined，下载卡片会回退到兜底链接
    });
  }, []);

  // 海报轮播自动播放（点击/长按暂停时不自动切换）。注册页单独使用更长的间隔，
  //  既尊重后台配置，又不至于像默认 2s 那样一闪而过。
  useEffect(() => {
    const posters = posterConfig?.posterImages?.filter(p => p.enabled) || [];
    if (posters.length <= 1) return;
    const autoPlay = posterConfig?.autoPlay ?? true;
    if (!autoPlay) return;
    if (isCarouselPaused) return; // 用户已暂停（点击或长按），不自动切换

    // 后台配置值与注册页下限取较大者
    const carouselInterval = Math.max(posterConfig?.interval ?? REGISTER_INTERVAL_FLOOR_MS, REGISTER_INTERVAL_FLOOR_MS);

    const interval = setInterval(() => {
      setCurrentPosterIndex(prev => (prev + 1) % posters.length);
    }, carouselInterval);

    return () => clearInterval(interval);
  }, [posterConfig, isCarouselPaused]);

  // 检查邮件服务状态（用于获取验证码倒计时时长）
  useEffect(() => {
    authAPI.getEmailServiceStatus().then(res => {
      if (res.success && res.data) {
        setCodeExpireSeconds(res.data.codeExpireSeconds || 60);
      }
    }).catch(() => {
      // 获取失败时使用默认倒计时（60s）
    });
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 邀请码验证（防抖）
  useEffect(() => {
    const code = normalizeInvitationCodeInput(formData.invitationCode);
    if (!code) {
      setInvitationCodeStatus('idle');
      setInviterUsername('');
      return;
    }

    if (code.length < 4) {
      setInvitationCodeStatus('idle');
      return;
    }

    setInvitationCodeStatus('validating');
    const timer = setTimeout(async () => {
      try {
        const res = await authAPI.validateInvitationCode(code);
        if (res.success && res.data?.valid) {
          setInvitationCodeStatus('valid');
          setInviterUsername(res.data.inviterUsername || '');
        } else {
          setInvitationCodeStatus('invalid');
          setInviterUsername('');
        }
      } catch {
        setInvitationCodeStatus('invalid');
        setInviterUsername('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.invitationCode]);

  const handleSendCode = useCallback(async () => {
    if (!formData.email) {
      setError('请先输入邮箱');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('邮箱格式不正确');
      return;
    }

    setError('');
    setSendingCode(true);

    try {
      await authAPI.sendRegisterCode(formData.email);
      setCodeSent(true);
      setCountdown(codeExpireSeconds);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败';
      setError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  }, [formData.email, codeExpireSeconds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证密码
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    if (formData.password.length < 8) {
      setError('密码长度至少为8位');
      return;
    }

    // 邮箱验证码必填（后端强制校验，确保邮箱真实有效）
    if (!formData.emailCode) {
      setError('请输入邮箱验证码');
      return;
    }

    setLoading(true);

    try {
      const invitationNormalized = normalizeInvitationCodeInput(formData.invitationCode);
      const response = await authAPI.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        emailCode: formData.emailCode,
        invitationCode: invitationNormalized || undefined,
      });

      if (response.success) {
        // 注册成功，跳转到登录页
        const successMessage = response.message || '注册成功，请登录';
        navigate('/login', {
          state: { message: successMessage }
        });
      } else {
        setError(response.message || '注册失败');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '注册失败，请稍后重试';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 启用的海报列表（用于轮播）
  const enabledPosters = posterConfig?.posterImages?.filter(p => p.enabled) || [];
  const posterCount = enabledPosters.length;
  // 索引可能在海报列表变化后越界，做一次安全裁剪
  const safePosterIndex = posterCount > 0 ? Math.min(currentPosterIndex, posterCount - 1) : 0;
  const currentPoster = enabledPosters[safePosterIndex];
  const hasMultiplePosters = posterCount > 1;

  // 轮播切换
  const goToPrevPoster = () =>
    setCurrentPosterIndex(safePosterIndex === 0 ? posterCount - 1 : safePosterIndex - 1);
  const goToNextPoster = () =>
    setCurrentPosterIndex((safePosterIndex + 1) % posterCount);

  // 点击图片暂停/恢复轮播（替代长按暂停）。长按仍由原生菜单负责保存图片。
  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const clearResumeTimer = () => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };
  // 长按保存后，延迟恢复自动轮播（不与点击的“手动暂停/恢复”冲突）
  const scheduleResumeCarousel = () => {
    clearResumeTimer();
    resumeTimerRef.current = setTimeout(() => setIsCarouselPaused(false), RESUME_DELAY_MS);
  };
  const togglePause = () => {
    // 手动切换：取消任何待恢复的计时，避免被自动恢复覆盖
    clearResumeTimer();
    setIsCarouselPaused(prev => !prev);
  };

  // 统一用 Pointer 事件处理触摸 + 鼠标，避免 touch/click 双触发
  const handlePointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    // 用户重新按压：取消待恢复的轮播（保持当前暂停/播放状态）
    clearResumeTimer();
    clearLongPressTimer();
    pressInfoRef.current = {
      time: Date.now(),
      x: e.clientX,
      y: e.clientY,
      moved: false,
      longPressFired: false,
    };
    // 长按检测：达到阈值立即暂停，保证保存时图片不切换
    longPressTimerRef.current = setTimeout(() => {
      if (pressInfoRef.current) pressInfoRef.current.longPressFired = true;
      setIsCarouselPaused(true);
    }, TAP_MAX_MS);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    const info = pressInfoRef.current;
    if (!info) return;
    const dx = e.clientX - info.x;
    const dy = e.clientY - info.y;
    if (Math.abs(dx) > TAP_MOVE_THRESHOLD || Math.abs(dy) > TAP_MOVE_THRESHOLD) {
      // 判定为拖拽/滑动，取消长按，且不再算作点击
      info.moved = true;
      clearLongPressTimer();
    }
  };
  const handlePointerUp = () => {
    clearLongPressTimer();
    const info = pressInfoRef.current;
    pressInfoRef.current = null;
    if (!info) return;
    // 长按保存：松手后延迟恢复（不切换暂停状态）
    if (info.longPressFired) {
      scheduleResumeCarousel();
      return;
    }
    // 拖拽/滑动：忽略
    if (info.moved) return;
    // 短按 = 点击：切换暂停/恢复
    if (Date.now() - info.time <= TAP_MAX_MS) {
      togglePause();
    }
  };
  const handlePointerCancel = () => {
    clearLongPressTimer();
    pressInfoRef.current = null;
  };
  // 桌面右键“图片另存为”时暂停，松开后延迟恢复
  const handleContextMenu = () => {
    setIsCarouselPaused(true);
    scheduleResumeCarousel();
  };
  const posterImageHandlers = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onContextMenu: handleContextMenu,
  };

  // 卸载时清理定时器，避免内存泄漏
  useEffect(() => {
    return () => {
      clearLongPressTimer();
      clearResumeTimer();
    };
  }, []);

  // 有邀请码的注册页面（美化版 + 移动端适配）
  if (hasInvitationCode && invitationCodeStatus !== 'invalid') {
    return (
      <div className="flex min-h-screen flex-col lg:flex-row bg-gradient-to-br from-violet-50 via-blue-50 to-cyan-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800">
        {/* 海报区域 - 移动端在顶部横向展示，桌面端在左侧 */}
        {currentPoster && (
          <div className="lg:w-1/2 relative">
            {/* 移动端：紧凑横幅 + 轮播 */}
            <div className="lg:hidden relative w-full poster-saveable">
              <img
                src={currentPoster.url}
                alt={currentPoster.title || '邀请海报'}
                className="w-full aspect-[3/2] object-cover"
                {...posterImageHandlers}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white pointer-events-none">
                <p className="font-bold text-lg">{currentPoster.title || '加入 Alita Planet'}</p>
                {currentPoster.description && (
                  <p className="text-white/80 text-sm mt-1">{currentPoster.description}</p>
                )}
              </div>
              {/* 移动端引导提示 */}
              <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 pointer-events-none z-10">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                轻点暂停 · 长按保存
              </div>
              {/* 暂停时的提示 */}
              {isCarouselPaused && hasMultiplePosters && (
                <div className="absolute top-3 left-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 pointer-events-none z-10">
                  <Pause className="w-3 h-3" />
                  已暂停 · 轻点恢复
                </div>
              )}
              {/* 轮播指示点 */}
              {hasMultiplePosters && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none z-10">
                  {enabledPosters.map((_, index) => (
                    <span
                      key={index}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${index === safePosterIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              )}
              {/* 轮播左右切换 */}
              {hasMultiplePosters && (
                <>
                  <button
                    type="button"
                    onClick={goToPrevPoster}
                    aria-label="上一张"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm z-10"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={goToNextPoster}
                    aria-label="下一张"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm z-10"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            {/* 移动端：下载按钮 - 轮播图下方，更醒目 */}
            <div className="lg:hidden px-4 py-3">
              <InvitationDownloadCard downloadUrl={appDownloadUrl} />
            </div>

            {/* 桌面端：轮播 + 下载按钮，垂直居中 */}
            <div className="hidden lg:flex lg:flex-col lg:justify-center lg:min-h-screen lg:px-10 lg:gap-6">
              <div className="relative w-full aspect-[3/4] poster-saveable overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={currentPoster.url}
                  alt={currentPoster.title || '邀请海报'}
                  className="absolute inset-0 w-full h-full object-cover"
                  {...posterImageHandlers}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-10 text-white pointer-events-none">
                  <h2 className="text-3xl font-bold mb-2">
                    {currentPoster.title || '加入 Alita Planet'}
                  </h2>
                  {currentPoster.description && (
                    <p className="text-white/80 text-lg">{currentPoster.description}</p>
                  )}
                </div>
                {/* 轮播指示点 */}
                {hasMultiplePosters && (
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none z-10">
                    {enabledPosters.map((_, index) => (
                      <span
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${index === safePosterIndex ? 'bg-white' : 'bg-white/40'}`}
                      />
                    ))}
                  </div>
                )}
                {/* 桌面端引导提示 */}
                <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none z-10">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  轻点暂停 · 长按保存
                </div>
                {/* 暂停时的提示 */}
                {isCarouselPaused && hasMultiplePosters && (
                  <div className="absolute top-6 left-6 bg-black/40 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full flex items-center gap-1 pointer-events-none z-10">
                    <Pause className="w-3.5 h-3.5" />
                    已暂停 · 轻点恢复
                  </div>
                )}
                {/* 轮播左右切换 */}
                {hasMultiplePosters && (
                  <>
                    <button
                      type="button"
                      onClick={goToPrevPoster}
                      aria-label="上一张"
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm z-10"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      type="button"
                      onClick={goToNextPoster}
                      aria-label="下一张"
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm z-10"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>
              {/* 桌面端：下载按钮 - 轮播图下方 */}
              <InvitationDownloadCard downloadUrl={appDownloadUrl} />
            </div>
          </div>
        )}

        {/* 无海报时的桌面端装饰 */}
        {!currentPoster && (
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
            <div className="flex flex-col items-center justify-center w-full p-12">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mb-6 shadow-2xl">
                <Sparkles className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-3">欢迎加入</h2>
              <p className="text-gray-500 dark:text-gray-400 text-center text-lg">
                接受好友邀请，注册即享专属奖励
              </p>
              <div className="mt-8 flex items-center gap-3 text-violet-600 dark:text-violet-400">
                <Gift className="w-5 h-5" />
                <span className="text-lg font-medium">双方各获得 token 奖励</span>
              </div>
            </div>
          </div>
        )}

        {/* 注册表单 */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md">
            {/* 头部 */}
            <div className="text-center mb-6 lg:mb-8 mt-2 lg:mt-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-sm font-medium mb-3 lg:mb-4">
                <Sparkles className="w-4 h-4" />
                接受邀请注册
              </div>
              <h1 className="text-xl lg:text-2xl font-bold mb-1">创建 Alita Planet 账号</h1>
              <p className="text-sm lg:text-base text-muted-foreground">开启你的 AI 之旅</p>
            </div>

            {/* 邀请人卡片 */}
            {invitationCodeStatus === 'valid' && inviterUsername && (
              <div className="mb-4 lg:mb-6 p-3 lg:p-4 rounded-xl bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 border border-violet-200/50 dark:border-violet-800/30">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md text-sm lg:text-base">
                    {inviterUsername[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs lg:text-sm text-muted-foreground">来自好友的邀请</p>
                    <p className="font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1 text-sm lg:text-base truncate">
                      <User className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
                      {inviterUsername}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      <span className="hidden sm:inline">验证通过</span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Card className="shadow-xl border-0 dark:border dark:border-gray-700">
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-3 lg:space-y-4 pt-5 lg:pt-6 px-4 lg:px-6">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="username">用户名</Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="给自己取个名字"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      disabled={loading}
                      minLength={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">邮箱</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* 邮箱验证码 */}
                  <div className="space-y-2">
                    <Label htmlFor="emailCode">邮箱验证码</Label>
                    <div className="flex gap-2">
                      <Input
                        id="emailCode"
                        type="text"
                        placeholder="请输入验证码"
                        value={formData.emailCode}
                        onChange={(e) => setFormData({ ...formData, emailCode: e.target.value })}
                        required
                        disabled={loading}
                        maxLength={6}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSendCode}
                        disabled={sendingCode || countdown > 0 || loading}
                        className="whitespace-nowrap min-w-[120px]"
                      >
                        {sendingCode ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : countdown > 0 ? (
                          `${countdown}秒后重发`
                        ) : codeSent ? (
                          '重新发送'
                        ) : (
                          <>
                            <Mail className="mr-1 h-4 w-4" />
                            获取验证码
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">密码</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="至少8位字符"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      disabled={loading}
                      minLength={8}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">确认密码</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="再次输入密码"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* 邀请码（已预填，只读展示） */}
                  <div className="space-y-2">
                    <Label htmlFor="invitationCode" className="flex items-center gap-1">
                      <Gift className="h-4 w-4 text-orange-500" />
                      邀请码
                    </Label>
                    <div className="relative">
                      <Input
                        id="invitationCode"
                        type="text"
                        value={formData.invitationCode}
                        readOnly
                        className="pr-8 bg-muted/50"
                      />
                      {invitationCodeStatus === 'validating' && (
                        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                      {invitationCodeStatus === 'valid' && (
                        <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-3 lg:space-y-4 px-4 lg:px-6 pb-5 lg:pb-6">
                  <Button type="submit" className="w-full h-11 lg:h-auto bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white shadow-lg text-base" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Sparkles className="mr-2 h-4 w-4" />
                    接受邀请并注册
                  </Button>

                  <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                    已有账号？
                    <Link to="/login" className="ml-1 text-violet-600 hover:underline dark:text-violet-400">
                      立即登录
                    </Link>
                  </div>
                </CardFooter>
              </form>
            </Card>
          </div>

          {/* 备案信息 */}
          <footer className="mt-8 py-4 text-center text-xs text-gray-400 dark:text-gray-500 space-y-1">
            <p>网站域名：yikaihui.com</p>
            <p>
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                京ICP备17037227号-1
              </a>
            </p>
          </footer>
        </div>
      </div>
    );
  }

  // 普通注册页面（无邀请码）
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="flex-1 flex items-center justify-center w-full max-w-md">
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">创建账号</CardTitle>
            <CardDescription className="text-center">
              注册 Alita Planet 账号
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">用户名</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={loading}
                  minLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">邮箱</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              {/* 邮箱验证码 */}
              <div className="space-y-2">
                <Label htmlFor="emailCode">邮箱验证码</Label>
                <div className="flex gap-2">
                  <Input
                    id="emailCode"
                    type="text"
                    placeholder="请输入验证码"
                    value={formData.emailCode}
                    onChange={(e) => setFormData({ ...formData, emailCode: e.target.value })}
                    required
                    disabled={loading}
                    maxLength={6}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendCode}
                    disabled={sendingCode || countdown > 0 || loading}
                    className="whitespace-nowrap min-w-[120px]"
                  >
                    {sendingCode ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : countdown > 0 ? (
                      `${countdown}秒后重发`
                    ) : codeSent ? (
                      '重新发送'
                    ) : (
                      <>
                        <Mail className="mr-1 h-4 w-4" />
                        获取验证码
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">密码</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="至少8位字符"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">确认密码</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="再次输入密码"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              {/* 邀请码（可选） */}
              <div className="space-y-2">
                <Label htmlFor="invitationCode" className="flex items-center gap-1">
                  <Gift className="h-4 w-4 text-orange-500" />
                  邀请码
                  <span className="text-xs text-muted-foreground">（选填）</span>
                </Label>
                <div className="relative">
                  <Input
                    id="invitationCode"
                    type="text"
                    placeholder="输入邀请码可获得额外奖励"
                    value={formData.invitationCode}
                    onChange={(e) => setFormData({ ...formData, invitationCode: e.target.value })}
                    disabled={loading}
                    maxLength={12}
                    className="pr-8"
                  />
                  {invitationCodeStatus === 'validating' && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {invitationCodeStatus === 'valid' && (
                    <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                  {invitationCodeStatus === 'invalid' && (
                    <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                  )}
                </div>
                {invitationCodeStatus === 'valid' && inviterUsername && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    ✨ 邀请人：{inviterUsername}，注册后双方各获得 token 奖励
                  </p>
                )}
                {invitationCodeStatus === 'invalid' && (
                  <p className="text-xs text-red-500">
                    邀请码无效或已达使用上限
                  </p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                注册
              </Button>

              <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                已有账号？
                <Link to="/login" className="ml-1 text-blue-600 hover:underline dark:text-blue-400">
                  立即登录
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* 备案信息 */}
      <footer className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
        <p>网站域名：yikaihui.com</p>
        <p>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            京ICP备17037227号-1
          </a>
        </p>
      </footer>
    </div>
  );
}
